import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';

// ---- SVG icons ----
const CalendarIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// ---- Choice card ----
function ChoiceCard({ icon, title, description, badge, features, onClick, index, accentColor }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 + index * 0.12, ease: 'easeOut' }}
      whileHover={{ y: -4, transition: { duration: 0.2, ease: 'easeOut' } }}
      whileTap={{ scale: 0.98 }}
      className={clsx(
        'group relative w-full text-left bg-white rounded-2xl shadow-soft-md border transition-all duration-300',
        'hover:shadow-soft-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
        'border-surface-100 hover:border-primary-200'
      )}
    >
      {/* Recommended badge */}
      {badge && (
        <div className="absolute -top-3 left-6">
          <span className="inline-flex items-center gap-1 bg-primary-500 text-white px-3 py-1 rounded-full text-caption font-bold shadow-colored">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Recommended
          </span>
        </div>
      )}

      <div className="p-6 flex items-start gap-5">
        {/* Icon container */}
        <div className={clsx(
          'flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110',
          accentColor === 'primary'
            ? 'bg-primary-50 text-primary-500 group-hover:bg-primary-100'
            : 'bg-accent-50 text-accent-500 group-hover:bg-accent-100'
        )}>
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-1">
            <h2 className="text-h3 text-surface-900 group-hover:text-primary-700 transition-colors duration-200">
              {title}
            </h2>
            <div className={clsx(
              'w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0',
              'bg-surface-100 text-surface-400 group-hover:bg-primary-50 group-hover:text-primary-500'
            )}>
              <ChevronRightIcon />
            </div>
          </div>

          <p className="text-body text-surface-500 mb-4 leading-relaxed">{description}</p>

          {/* Feature bullets */}
          {features && features.length > 0 && (
            <ul className="space-y-1.5">
              {features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <div className={clsx(
                    'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
                    accentColor === 'primary' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                  )}>
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-body-sm text-surface-600">{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Bottom accent line — animates on hover */}
      <div className={clsx(
        'absolute bottom-0 left-0 right-0 h-0.5 rounded-b-2xl scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left',
        accentColor === 'primary' ? 'bg-primary-400' : 'bg-accent-400'
      )} aria-hidden="true" />
    </motion.button>
  );
}

// ---- Main component ----
function CreateCliqqType() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-surface-50 font-sans">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-white/15 text-white hover:bg-white/25 flex items-center justify-center transition-colors duration-200"
          aria-label="Go back"
        >
          <ChevronLeftIcon />
        </button>
        <span className="text-white text-body font-bold tracking-wide">CliQQ</span>
        <div className="w-9" aria-hidden="true" />
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-xl mx-auto px-4 mt-10 mb-16"
      >
        <div className="bg-white rounded-2xl shadow-soft-md border border-surface-100 overflow-hidden">
          {/* Card header */}
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-7 py-7 border-b border-surface-100 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="w-14 h-14 rounded-2xl bg-primary-500 text-white flex items-center justify-center shadow-colored mx-auto mb-4"
            >
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </motion.div>
            <h1 className="text-h2 text-surface-900 mb-1">Create a New CliQQ</h1>
            <p className="text-body text-surface-500">What would you like to create?</p>
          </div>

          {/* Options */}
          <div className="px-6 py-8 space-y-6">
            {/* Standard CliQQ */}
            <ChoiceCard
              index={0}
              icon={<CalendarIcon />}
              title="Standard CliQQ"
              description="One-off events and direct meetups. Perfect for a single occasion or spontaneous gathering."
              badge="Recommended"
              accentColor="primary"
              features={[
                'Set a specific date and time',
                'Invite up to unlimited guests',
                'Built-in ticketing and check-in',
              ]}
              onClick={() => navigate('/create-event')}
            />

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-surface-100" />
              <span className="text-body-sm font-semibold text-surface-400 px-2">or</span>
              <div className="flex-1 h-px bg-surface-100" />
            </div>

            {/* Group CliQQ */}
            <ChoiceCard
              index={1}
              icon={<UsersIcon />}
              title="Group CliQQ"
              description="Persistent groups that can host multiple CliQQs. Build a community around shared interests."
              accentColor="accent"
              features={[
                'Host unlimited recurring events',
                'Manage members and roles',
                'Group chat and announcements',
              ]}
              onClick={() => navigate('/create-group-cliqq')}
            />
          </div>

          {/* Progress bar */}
          <div className="px-7 py-4 bg-surface-50 border-t border-surface-100">
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-caption font-semibold text-primary-600">Join</span>
              </div>
              <div className="h-px w-12 bg-primary-300 rounded-full" />
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">2</span>
                </div>
                <span className="text-caption font-semibold text-primary-600">My page</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default CreateCliqqType;
